import { queryOptions } from "@tanstack/react-query";
import {
  checkMyUserInfo,
  getAllWorkspaceRequests,
  getAvailableOrganisationsToJoin,
  getMyInvites,
  getMyUserInfo,
  getOrganisationData,
  getOrganisationDomains,
  getOrganisationUsers,
  getUserData,
  getUsersWithAccessToTenderPackage,
  listMyNotifications,
  listMyRequests,
  listMyUserOrganisationsInfo,
  myWorkspaceJoinRequests,
} from "@/api/user/index.ts";
import {
  getTenderPackage,
  getTenderPackages,
  getPackageViews,
  listMyViewedTenderPackages,
} from "./api/tenderPackage/index.ts";
import {
  getAssessmentById,
  getAssessmentCollection,
  getPackageAssessments,
  getPackageMetrics,
  getPackageResults,
  listAssessmentCollections,
  listPublishedAssessments,
  listQuestions,
} from "./api/assessment/index.ts";
import { getWorkspace } from "./context/index.ts";
import { sortDesc } from "./service/date.ts";

export const checkUserQuery = queryOptions({
  queryKey: ["user"],
  queryFn: checkMyUserInfo,
  retry: 1,
});

export const userQuery = queryOptions({
  queryKey: ["user"],
  queryFn: getMyUserInfo,
  retry: 1,
});

export const availableOrgsQuery = queryOptions({
  queryKey: ["getAvailableOrgsToJoin"],
  queryFn: getAvailableOrganisationsToJoin,
});

export const orgInfoQuery = (orgId: string) =>
  queryOptions({
    queryKey: [orgId, "getOrganisationData"],
    queryFn: getOrganisationData,
  });

export const workspaceJoinRequestsQuery = (orgId: string) =>
  queryOptions({
    queryKey: [orgId, "myWorkspaceJoinRequests"],
    queryFn: myWorkspaceJoinRequests,
  });

export const listCollectionsQuery = (orgId: string, workspaceId: string) =>
  queryOptions({
    queryKey: [orgId, workspaceId, "collections"],
    queryFn: listAssessmentCollections,
  });

export const getCollectionQuery = (collectionId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      "collections",
      collectionId,
    ] as const,
    queryFn: async (args) => {
      const result = await getAssessmentCollection(args);
      const collection = result.data;
      // Put assessments into cache so navigation is instant
      for (const data of collection.assessments || []) {
        const assQuery = getAssessmentQuery({
          collectionId,
          assessmentId: data.id,
        });
        args.client.setQueryData(assQuery.queryKey, { data });
      }
      return collection;
    },
  });
};

type GetAssessmentArgs = {
  collectionId: string;
  assessmentId: string;
};

export const getAssessmentQuery = ({
  collectionId,
  assessmentId,
}: GetAssessmentArgs) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      "collections",
      collectionId,
      "assessments",
      assessmentId,
    ],
    queryFn: getAssessmentById,
  });
};

export const getQuestionsQuery = (orgId: string, workspaceId: string) =>
  queryOptions({
    queryKey: [orgId, workspaceId, "questions"],
    queryFn: listQuestions,
  });

export const getOrgDomainsQuery = (orgId: string) =>
  queryOptions({
    queryKey: [orgId, "getOrganisationDomains"],
    queryFn: getOrganisationDomains,
    select: ({ data }) => data,
  });

export const getAllUsersInOrgQuery = (
  orgId: string,
  includeInvites: boolean = false
) =>
  queryOptions({
    queryKey: [
      orgId,
      includeInvites
        ? "getAllUsersInOrganisationWithInvites"
        : "getAllUsersInOrganisation",
    ],
    queryFn: getOrganisationUsers(includeInvites),
  });

export const getUserProfileQuery = (orgId: string, userId: string) =>
  queryOptions({
    queryKey: [orgId, userId, "getUserById"],
    queryFn: getUserData,
  });

export const getAllWorkspaceJoinRequestsQuery = (orgId: string) =>
  queryOptions({
    queryKey: [orgId, "getAllWorkspaceRequests"],
    queryFn: getAllWorkspaceRequests,
    select: ({ data }) => data,
  });

export const getMyInvitesQuery = queryOptions({
  queryKey: ["getMyInvites"],
  queryFn: getMyInvites,
});

export const getUsersWithAccessToTenderPackageQuery = (
  orgId: string,
  workspaceId: string,
  assessmentPackageId: string
) =>
  queryOptions({
    queryKey: [
      orgId,
      workspaceId,
      assessmentPackageId,
      "getUsersWithAccessToTenderPackage",
    ],
    queryFn: getUsersWithAccessToTenderPackage,
  });

export const listPackagesQuery = () => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, "packages"],
    queryFn: getTenderPackages,
    select: (data) => data.data,
  });
};

export const getPackageQuery = (packageId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, "getPackage", packageId],
    queryFn: getTenderPackage,
  });
};

export const listPublishedAssessmentsQuery = () => {
  const { organisation } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, "publishedAssessments"],
    queryFn: listPublishedAssessments,
    select: (data) =>
      data.data.toSorted((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      ),
  });
};

export const getPackageAssessmentsQuery = (packageId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [
      organisation.id,
      workspace.id,
      "packages",
      packageId,
      "assessments",
    ],
    queryFn: getPackageAssessments,
    select: (data) =>
      data.data.toSorted((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      ),
  });
};

export const getPackageMetricsQuery = (packageId: string) => {
  const { organisation } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, packageId, "metrics"],
    queryFn: getPackageMetrics,
  });
};

export const getPackageViewsQuery = (packageId: string) => {
  const { organisation } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, packageId, "views"],
    queryFn: getPackageViews,
  });
};

export const listMyViewedTenderPackagesQuery = ({
  organisationId,
  workspaceId,
}: {
  organisationId: string;
  workspaceId: string;
}) =>
  queryOptions({
    queryKey: [organisationId, workspaceId, "listMyViewedTenderPackages"],
    queryFn: listMyViewedTenderPackages,
    select: ({ data }) =>
      data.toSorted((a, b) => {
        if (!a.submissionDeadline || !b.submissionDeadline) return -1;
        return sortDesc(a.submissionDeadline, b.submissionDeadline);
      }),
  });

export const getPackageResultsQuery = (packageId: string) => {
  const { organisation, workspace } = getWorkspace();
  return queryOptions({
    queryKey: [organisation.id, workspace.id, "packages", packageId, "results"],
    queryFn: getPackageResults,
    select: (data) => data.data,
  });
};

export const listAllMyOrganisationsInfoQuery = queryOptions({
  queryKey: ["userAllOrgsAndInfo"],
  queryFn: listMyUserOrganisationsInfo,
  select: (data) => data.data,
});

export const listMyRequestsQuery = queryOptions({
  queryKey: ["listWorkspaceRequests"],
  queryFn: listMyRequests,
  select: (data) => data.data,
});

export const listMyNotificationsQuery = (
  organisationId: string,
  workspaceId?: string
) =>
  queryOptions({
    queryKey: [organisationId, workspaceId, "listMyNotifications"],
    queryFn: listMyNotifications,
    select: (data) => data.data,
  });
